import React, { useMemo, useState } from 'react';
import { useDisclosure } from '@chakra-ui/react';
import { ReportingContainerBaseConfig } from '../../../../base';
import { SnippetInputNewController } from './snippetNewInterface';
import { useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import {
    SnippetInputNewContainerProps,
    SnippetInputNewViewProps,
} from './snippetNewProps';
import { assertNever } from '@varos/util-typescript';
import { SnippetNewFormValues } from './snippetNewModel';
import { useForm } from 'react-hook-form';
import { SnippetNewFormValuesSchema } from './snippetNewSchema';
import { zodResolver } from '@hookform/resolvers/zod';

export function createSnippetInputNewContainer(
    config: ReportingContainerBaseConfig,
    controller: SnippetInputNewController
): React.FC<SnippetInputNewContainerProps> {
    const {
        context: { useContext },
        repository: { research: repository },
    } = config;

    return ({ as: View, ...containerProps }) => {
        const context = useContext();
        const mutation = {
            snippet: {
                create: repository.snippet.useCreate(context),
            },
        };

        const form = useForm<SnippetNewFormValues>({
            defaultValues: {
                title: null,
                content: null,
            },
            resolver: zodResolver(SnippetNewFormValuesSchema),
            reValidateMode: 'onChange',
        });

        const values = form.watch();

        const props = controller.useProps(context, {
            field: {
                title: {
                    value: values.title,
                    onChange(value) {
                        form.setValue('title', value, { shouldTouch: true });
                    },
                },
                content: {
                    value: values.content,
                    onChange(value) {
                        form.setValue('content', value, { shouldTouch: true });
                    },
                },
            },
            isSubmitting: mutation.snippet.create.isLoading,
            onClose() {
                form.reset();
                containerProps.onClose();
            },
            onSave: form.handleSubmit(async (values) => {
                console.log('saving new snippet', values);
                const parsed = SnippetNewFormValuesSchema.parse(values);
                const result = await mutation.snippet.create.mutateAsync({
                    title: parsed.title ?? undefined,
                    content: parsed.content,
                });
                console.log('saved new snippet', result);
                await containerProps.onSave(result);
            }),
        });
        return <View {...props.input} />;
    };
}
